<template>
  <div class="pycad-vtk-frame volume-viewer-frame" tabindex="0">
    <div class="pycad-vtk-stage" data-testid="two-view-container">
      <v-progress-linear
        v-if="isImageLoading"
        indeterminate
        class="pycad-loading"
        height="2"
        color="primary"
      />
      <div class="vtk-sub-container">
        <vtk-volume-view
          class="vtk-view"
          ref="vtkView"
          data-testid="vtk-view vtk-volume-view"
          :view-id="viewId"
          :image-id="currentImageID"
          :view-direction="viewDirection"
          :view-up="viewUp"
        >
          <vtk-base-volume-representation
            v-if="volumeRepMounted && currentImageID"
            :view-id="viewId"
            :image-id="currentImageID"
          ></vtk-base-volume-representation>
          <vtk-poly-data-representation
            v-for="modelId in modelIds"
            :key="modelId"
            :model-id="modelId"
          />
          <VtkMPRPlanesRepresentation
            v-if="mprPlanesVisible && mprPlanesMounted"
            :view-id="viewId"
            :image-id="currentImageID"
          />
          <vtk-orientation-marker></vtk-orientation-marker>
          <component
            v-if="sliceTools.crop"
            :is="sliceTools.crop"
            :view-id="viewId"
            :image-id="currentImageID"
          />
          <slot></slot>
        </vtk-volume-view>
      </div>

      <VolumeRenderingBootOverlay :visible="volumeRenderingBooting" />

      <!-- Volume-rendering disabled placeholder.
           Hidden when meshes are loaded so they remain visible. -->
      <div
        v-if="
          currentImageID && !volumeRenderingEnabled && modelIds.length === 0
        "
        class="pycad-vr-placeholder"
        @click.stop="enableVolumeRendering"
      >
        <div class="pycad-vr-placeholder__card">
          <i class="mdi mdi-cube-outline pycad-vr-placeholder__icon"></i>
          <div class="pycad-vr-placeholder__title">Volume rendering off</div>
          <div class="pycad-vr-placeholder__subtitle">
            Click to render this scan in 3D
          </div>
          <button
            type="button"
            class="pycad-vr-placeholder__btn"
            @click.stop="enableVolumeRendering"
          >
            <i class="mdi mdi-power"></i>
            <span>Enable</span>
          </button>
        </div>
      </div>

      <PycadViewerOverlay
        label="3D"
        label-color="#ff9900"
        :patient-name="currentImageMetadata.name"
        :patient-meta="presetName"
        :show-slice="false"
        :show-window-level="false"
        bottom-pad="12px"
      >
        <template #top-right>
          <PycadViewControls
            :is-maximized="isMaximized"
            @reset="resetCamera"
            @toggle-maximize="toggleMaximize"
          >
            <button
              v-if="currentImageID"
              type="button"
              class="pycad-vr-toggle"
              :class="{ 'pycad-vr-toggle--on': volumeRenderingEnabled }"
              :title="
                volumeRenderingEnabled
                  ? 'Turn off volume rendering'
                  : 'Turn on volume rendering'
              "
              @click="toggleVolumeRendering"
            >
              <i class="mdi mdi-cube-outline"></i>
            </button>
            <button
              v-if="currentImageID && crosshairsActive"
              type="button"
              class="pycad-vr-toggle"
              :class="{ 'pycad-vr-toggle--on': mprPlanesEnabled }"
              :title="
                mprPlanesEnabled
                  ? 'Hide MPR planes in 3D'
                  : 'Show MPR planes in 3D'
              "
              @click="toggleMprPlanes"
            >
              <i class="mdi mdi-axis-arrow"></i>
            </button>
          </PycadViewControls>
        </template>
        <template #bottom-left>
          <div
            v-if="currentImageID && volumeRenderingEnabled"
            class="pycad-vr-quality-bar"
          >
            <button
              v-for="p in vrPresets"
              :key="p"
              type="button"
              class="pycad-vr-quality-btn"
              :class="{ 'pycad-vr-quality-btn--active': vrPreset === p }"
              :title="vrPresetLabels[p]"
              @click="setVRPreset(p)"
            >
              {{ vrPresetShortLabels[p] }}
            </button>
          </div>
        </template>
        <template #bottom-right>
          <div class="pycad-view-switcher-wrapper">
            <ViewTypeSwitcher :view-id="viewId" :image-id="currentImageID" />
          </div>
        </template>
      </PycadViewerOverlay>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, toRefs, computed, watch, onBeforeUnmount } from 'vue';
import { storeToRefs } from 'pinia';
import { useCurrentImage } from '@/src/composables/useCurrentImage';
import { LPSAxisDir } from '@/src/types/lps';
import VtkVolumeView from '@/src/components/vtk/VtkVolumeView.vue';
import { VtkViewApi } from '@/src/types/vtk-types';
import VtkBaseVolumeRepresentation from '@/src/components/vtk/VtkBaseVolumeRepresentation.vue';
import VtkPolyDataRepresentation from '@/src/components/vtk/VtkPolyDataRepresentation.vue';
import VtkMPRPlanesRepresentation from '@/src/components/vtk/VtkMPRPlanesRepresentation.vue';
import { useModelStore } from '@/src/store/datasets-models';
import { useToolStore } from '@/src/store/tools';
import { Tools } from '@/src/store/tools/types';
import { useViewAnimationListener } from '@/src/composables/useViewAnimationListener';
import { enabledSliceTools } from '@/src/features/registry';
import { useWebGLWatchdog } from '@/src/composables/useWebGLWatchdog';
import VtkOrientationMarker from '@/src/components/vtk/VtkOrientationMarker.vue';
import PycadViewerOverlay from '@/src/components/pycad/PycadViewerOverlay.vue';
import PycadViewControls from '@/src/components/pycad/PycadViewControls.vue';
import useVolumeColoringStore from '@/src/store/view-configs/volume-coloring';
import { useResetViewsEvents } from '@/src/components/tools/ResetViews.vue';
import { onVTKEvent } from '@/src/composables/onVTKEvent';
import { useViewStore } from '@/src/store/views';
import ViewTypeSwitcher from '@/src/components/ViewTypeSwitcher.vue';
import { usePycadUiStore } from '@/src/store/pycad-ui';
import {
  useVRQualityStore,
  getPresetCVRConfig,
  VR_PRESET_LABELS,
  type VRQualityPreset,
} from '@/src/store/volumeRenderingQuality';
import VolumeRenderingBootOverlay from '@/src/components/pycad/VolumeRenderingBootOverlay.vue';
import { useDeferredVolumeRenderingMount } from '@/src/composables/useDeferredVolumeRenderingMount';

interface Props {
  viewId: string;
}

interface VolumeViewerOptions {
  viewDirection: LPSAxisDir;
  viewUp: LPSAxisDir;
}

const vtkView = ref<VtkViewApi>();

const sliceTools = enabledSliceTools();

const props = defineProps<Props>();
const { viewId } = toRefs(props);

const viewStore = useViewStore();
const viewInfo = computed(() => viewStore.getView(viewId.value)!);
const viewType = computed(() => viewInfo.value.type);
const viewOptions = computed(
  () => viewInfo.value.options as VolumeViewerOptions
);
const viewDirection = computed(() => viewOptions.value.viewDirection);
const viewUp = computed(() => viewOptions.value.viewUp);

function resetCamera() {
  if (!vtkView.value) return;
  vtkView.value.resetCamera();
  vtkView.value.renderer.updateLightsGeometryToFollowCamera();
}

useResetViewsEvents().onClick(resetCamera);

useWebGLWatchdog(vtkView);
useViewAnimationListener(vtkView, viewId, viewType);

const {
  currentImageID,
  currentImageData,
  currentImageMetadata,
  isImageLoading,
} = useCurrentImage();

const modelStore = useModelStore();
const { idList: modelIds } = storeToRefs(modelStore);

onVTKEvent(currentImageData, 'onModified', () => {
  vtkView.value?.requestRender();
});

const coloringStore = useVolumeColoringStore();
const coloringConfig = computed(() =>
  coloringStore.getConfig(viewId.value, currentImageID.value)
);
const presetName = computed(
  () => coloringConfig.value?.transferFunction.preset.replace(/-/g, ' ') ?? ''
);

const { activeView, isActiveViewMaximized } = storeToRefs(viewStore);
const isMaximized = computed(
  () => activeView.value === viewId.value && isActiveViewMaximized.value
);

function toggleMaximize() {
  viewStore.setActiveView(viewId.value);
  viewStore.toggleActiveViewMaximized();
}

// --- Volume-rendering opt-in --- //
// Default off: a freshly loaded scan only shows the 2D MPRs. The user opts
// into 3D volume rendering via the placeholder click, the VR toggle on the
// 3D view controls, or the Rendering panel. Reset to off whenever a new
// image is loaded so each new scan starts in 2D-only.
const pycadUi = usePycadUiStore();
const { volumeRenderingEnabled, mprPlanesEnabled } = storeToRefs(pycadUi);

const { volumeRepMounted, volumeRenderingBooting } =
  useDeferredVolumeRenderingMount(volumeRenderingEnabled, vtkView);

function applyCurrentPreset() {
  if (!currentImageID.value) return;
  // Only patch an existing config (covers stale configs from older sessions).
  // A fresh config already picks up the persisted preset via
  // defaultVolumeColorConfig(), and creating one here would make
  // useVolumeColoringInitializer skip resetToDefaultColoring, leaving the
  // volume without a color/opacity transfer function (blank 3D view).
  if (!coloringStore.getConfig(viewId.value, currentImageID.value)) return;
  const cvr = getPresetCVRConfig(vrQualityStore.preset);
  coloringStore.updateCVRParameters(viewId.value, currentImageID.value, cvr);
}

function enableVolumeRendering() {
  applyCurrentPreset();
  pycadUi.setVolumeRenderingEnabled(true);
}

function toggleVolumeRendering() {
  if (!volumeRenderingEnabled.value) applyCurrentPreset();
  pycadUi.toggleVolumeRendering();
}

function toggleMprPlanes() {
  pycadUi.toggleMprPlanes();
}

watch(currentImageID, (newId, oldId) => {
  if (newId !== oldId) pycadUi.setVolumeRenderingEnabled(false);
});

// --- VR quality presets ---
const vrQualityStore = useVRQualityStore();
const vrPreset = computed(() => vrQualityStore.preset);
const vrPresets: VRQualityPreset[] = ['performance', 'balanced', 'cinematic'];
const vrPresetLabels = VR_PRESET_LABELS;
const vrPresetShortLabels: Record<VRQualityPreset, string> = {
  performance: 'Perf',
  balanced: 'Balanced',
  cinematic: 'Cine',
};

function setVRPreset(p: VRQualityPreset) {
  vrQualityStore.setPreset(p);
  if (!currentImageID.value) return;
  const cvr = getPresetCVRConfig(p);
  coloringStore.updateCVRParameters(viewId.value, currentImageID.value, cvr);
}

// --- Crosshair-driven MPR planes in 3D --- //
// While the Crosshairs tool is active we show three reslice planes through
// the volume at the crosshair position. The user can toggle their visibility
// with the per-view button above; the toggle itself is only relevant (and
// only rendered) while the crosshair tool is active.
const toolStore = useToolStore();
const crosshairsActive = computed(
  () => toolStore.currentTool === Tools.Crosshairs
);
const mprPlanesVisible = computed(
  () =>
    crosshairsActive.value && mprPlanesEnabled.value && !!currentImageID.value
);

// Deferred mount of the 3D MPR planes.
// Mounting VtkMPRPlanesRepresentation creates three vtkImageResliceMappers
// that compile shaders and upload textures on their first render. Doing that
// inline with the crosshair activation freezes the main thread for a few
// hundred milliseconds and the user can't drag the crosshair widget until
// it's done. We let the browser paint the crosshair UI first and schedule
// the planes mount during the next idle slice (with a hard 500ms cap so the
// planes never feel "missing" on a busy page). Toggling the tool off
// short-circuits the pending mount so we never attach actors to a renderer
// that's about to be unmounted.
const mprPlanesMounted = ref(false);
let pendingMountHandle: number | null = null;

function cancelPendingMount() {
  if (pendingMountHandle == null) return;
  const idle = (
    window as Window & {
      cancelIdleCallback?: (handle: number) => void;
    }
  ).cancelIdleCallback;
  if (idle) idle(pendingMountHandle);
  else window.clearTimeout(pendingMountHandle);
  pendingMountHandle = null;
}

watch(
  mprPlanesVisible,
  (visible) => {
    cancelPendingMount();
    if (!visible) {
      mprPlanesMounted.value = false;
      return;
    }
    const idle = (
      window as Window & {
        requestIdleCallback?: (
          cb: () => void,
          opts?: { timeout: number }
        ) => number;
      }
    ).requestIdleCallback;
    if (idle) {
      pendingMountHandle = idle(
        () => {
          pendingMountHandle = null;
          mprPlanesMounted.value = true;
        },
        { timeout: 500 }
      );
    } else {
      pendingMountHandle = window.setTimeout(() => {
        pendingMountHandle = null;
        mprPlanesMounted.value = true;
      }, 100);
    }
  },
  { immediate: true }
);

onBeforeUnmount(cancelPendingMount);
</script>

<style scoped src="@/src/components/styles/vtk-view.css"></style>
<style scoped src="@/src/components/styles/utils.css"></style>
<style scoped src="@/src/components/pycad/pycad-vtk-view.css"></style>

<style scoped>
.volume-viewer-frame {
  background-color: black;
}

.pycad-view-switcher-wrapper {
  padding: 4px;
  background: rgba(5, 5, 5, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.45);
}

/* Volume-rendering-off placeholder */
.pycad-vr-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  cursor: pointer;
  background: radial-gradient(
    ellipse at center,
    rgba(30, 32, 40, 0.55) 0%,
    rgba(5, 5, 5, 0.85) 100%
  );
  transition: background 0.2s ease;
}

.pycad-vr-placeholder:hover {
  background: radial-gradient(
    ellipse at center,
    rgba(40, 44, 56, 0.65) 0%,
    rgba(5, 5, 5, 0.85) 100%
  );
}

.pycad-vr-placeholder__card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 20px 28px;
  border-radius: 12px;
  background: rgba(22, 24, 32, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.55);
  font-family: 'Inter', system-ui, sans-serif;
  color: #d9d9d9;
  text-align: center;
  max-width: 260px;
}

.pycad-vr-placeholder__icon {
  font-size: 32px;
  color: #ff9900;
  opacity: 0.85;
}

.pycad-vr-placeholder__title {
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
}

.pycad-vr-placeholder__subtitle {
  font-size: 12px;
  color: #a0a0a0;
  margin-bottom: 4px;
}

.pycad-vr-placeholder__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 8px;
  background: rgba(255, 153, 0, 0.18);
  border: 1px solid rgba(255, 153, 0, 0.5);
  color: #ff9900;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  outline: none;
  transition: all 0.15s ease;
}

.pycad-vr-placeholder__btn:hover {
  background: rgba(255, 153, 0, 0.28);
  border-color: rgba(255, 153, 0, 0.65);
}

.pycad-vr-placeholder__btn .mdi {
  font-size: 14px;
}

/* VR on/off toggle in the per-view controls pill */
.pycad-vr-toggle {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  background: transparent;
  border: 1px solid transparent;
  color: #a0a0a0;
  cursor: pointer;
  outline: none;
  padding: 0;
  font-family: inherit;
  transition: all 0.15s ease;
}

.pycad-vr-toggle:hover {
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
}

.pycad-vr-toggle--on {
  background: rgba(255, 153, 0, 0.18);
  border-color: rgba(255, 153, 0, 0.5);
  color: #ff9900;
  box-shadow: 0 0 8px rgba(255, 153, 0, 0.2);
}

.pycad-vr-toggle .mdi {
  font-size: 15px;
  line-height: 1;
}

.pycad-vr-quality-bar {
  display: inline-flex;
  gap: 2px;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 8px;
  padding: 2px;
  backdrop-filter: blur(6px);
}

.pycad-vr-quality-btn {
  padding: 3px 10px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #a0a0a0;
  font-size: 10px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  outline: none;
  transition: all 0.15s ease;
  letter-spacing: 0.02em;
}

.pycad-vr-quality-btn:hover {
  color: #d9d9d9;
  background: rgba(255, 255, 255, 0.06);
}

.pycad-vr-quality-btn--active {
  background: rgba(255, 153, 0, 0.18);
  border-color: rgba(255, 153, 0, 0.45);
  color: #ff9900;
}
</style>
